import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { FileCheck2, Download, ArrowLeft, Lock, ShieldCheck } from "lucide-react";
import { t } from "../lib/i18n";

export default function NdaSign() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dealText, setDealText] = useState("");
  const [meta, setMeta] = useState(null); // { document_version, document_hash, project_code }
  const [signed, setSigned] = useState(null); // signature object once signed / already signed
  const [accepted, setAccepted] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [textR, statusR] = await Promise.all([
          api.get(`/nda/deal-text?company_id=${companyId}`),
          api.get(`/nda/status?company_id=${companyId}`),
        ]);
        setDealText(textR.data.text);
        setMeta({
          document_version: textR.data.document_version,
          document_hash: textR.data.document_hash,
          project_code: textR.data.project_code,
        });
        if (statusR.data.signed) setSigned(statusR.data.signature);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo cargar el NDA");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const submit = async () => {
    if (!accepted) {
      toast.error(t("Marca la casilla para aceptar los términos."));
      return;
    }
    if (!typedName.trim()) {
      toast.error(t("Escribe tu nombre completo para firmar."));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/nda/sign", {
        company_id: companyId,
        typed_full_name: typedName.trim(),
      });
      setSigned(r.data.signature);
      toast.success(r.data.already_signed ? "Ya tenías este NDA firmado" : "NDA firmado correctamente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error firmando el NDA");
    } finally {
      setSubmitting(false);
    }
  };

  const backToDeal = () => {
    if (user?.role === "investor") navigate(`/investor/opportunity/${companyId}`);
    else navigate(`/searcher/company/${companyId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <span className="font-mono-label text-muted-foreground animate-pulse">{t("Cargando NDA…")}</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <button
          type="button"
          onClick={backToDeal}
          className="font-mono-label text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6"
          data-testid="nda-back-btn"
        >
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver a la ficha")}
        </button>

        <span className="font-mono-label text-gold">{t("NDA · Acuerdo de Confidencialidad y No Elusión")}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3 mb-3">
          {signed ? t("NDA firmado") : t("Firma el NDA para avanzar")}
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          {t("Este acuerdo protege al vendedor, reconoce a Relevo como originador de la oportunidad y te da acceso a información privilegiada del proyecto")}{" "}<span className="font-medium">{meta?.project_code}</span>{t(". La aceptación es electrónica y genera un PDF descargable con validez probatoria.")}
        </p>

        {signed ? (
          <SignedPanel signed={signed} onBack={backToDeal} />
        ) : (
          <SignPanel
            dealText={dealText}
            meta={meta}
            user={user}
            accepted={accepted}
            setAccepted={setAccepted}
            typedName={typedName}
            setTypedName={setTypedName}
            submit={submit}
            submitting={submitting}
          />
        )}
      </div>
    </Layout>
  );
}

const SignPanel = ({ dealText, meta, user, accepted, setAccepted, typedName, setTypedName, submit, submitting }) => (
  <>
    <div
      className="mt-10 hairline bg-card p-7 max-h-[420px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed"
      data-testid="nda-text-content"
    >
      {dealText}
    </div>

    <div className="mt-3 flex items-center justify-between font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
      <span>{t("Versión:")}{" "}{meta?.document_version}</span>
      <span data-testid="nda-doc-hash">{t("Hash:")}{" "}{meta?.document_hash?.slice(0, 16)}…</span>
    </div>

    <div className="mt-8 hairline p-6 bg-bone space-y-5">
      <div className="flex items-start gap-3">
        <Checkbox
          id="nda-accept"
          checked={accepted}
          onCheckedChange={(v) => setAccepted(Boolean(v))}
          className="rounded-none mt-1"
          data-testid="nda-accept-checkbox"
        />
        <label htmlFor="nda-accept" className="text-sm cursor-pointer select-none leading-relaxed">
          {t("He leído y acepto el Acuerdo de Confidencialidad y No Elusión. Firmo escribiendo mi nombre completo en el campo inferior. Reconozco que esta aceptación tiene efectos jurídicos y queda registrada con mi email verificado, IP, navegador y fecha y hora.")}
        </label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="nda-typed-name" className="font-mono-label text-muted-foreground">
          {t("Escribe tu nombre completo para firmar (debe coincidir con «")}{user?.name}»)
        </Label>
        <Input
          id="nda-typed-name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder={user?.name || t("Nombre y apellidos")}
          className="rounded-none h-11 font-serif-display text-lg"
          data-testid="nda-typed-name-input"
          autoComplete="off"
        />
      </div>
    </div>

    <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
      <span className="font-mono-label text-muted-foreground flex items-center gap-2">
        <Lock className="w-3 h-3" />{" "}{t("Registro auditable + PDF descargable")}
      </span>
      <Button
        onClick={submit}
        disabled={submitting || !accepted || !typedName.trim()}
        className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8 disabled:opacity-50"
        data-testid="nda-submit-btn"
      >
        {submitting ? t("Firmando…") : t("Firmar NDA")}
      </Button>
    </div>
  </>
);

const SignedPanel = ({ signed, onBack }) => {
  const pdfHref = `${API}/nda/${signed.signature_id}/pdf`;
  return (
    <div className="mt-10 hairline bg-card p-8 space-y-6" data-testid="nda-signed-panel">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-gold" />
        </div>
        <div className="flex-1">
          <span className="font-mono-label text-gold">{t("Firma registrada")}</span>
          <h2 className="font-serif-display text-2xl mt-1">{t("Acceso concedido a")}{" "}{signed.project_code}</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {t("Firmado el")}{" "}{new Date(signed.signed_at).toLocaleString("es-ES")}{" "}{t("por")}{" "}<span className="font-medium">{signed.signer_name}</span>.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-0 hairline border-r-0 border-b-0">
        <MetaRow label={t("ID de firma")} value={signed.signature_id} testid="nda-signature-id" />
        <MetaRow label={t("Documento")} value={signed.document_version} />
        <MetaRow label={t("Hash del documento")} value={signed.document_hash.slice(0, 24) + "…"} />
        <MetaRow label={t("IP registrada")} value={signed.ip} />
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <a
          href={pdfHref}
          target="_blank"
          rel="noreferrer"
          className="hairline px-4 py-2 font-mono-label bg-bone hover:bg-card inline-flex items-center gap-2"
          data-testid="nda-download-pdf"
        >
          <Download className="w-3 h-3" />{" "}{t("Descargar PDF firmado")}
        </a>
        <Button
          onClick={onBack}
          className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-11 px-6"
          data-testid="nda-back-to-deal"
        >
          <FileCheck2 className="w-4 h-4 mr-2" />{" "}{t("Volver al deal")}
        </Button>
      </div>
    </div>
  );
};

const MetaRow = ({ label, value, testid }) => (
  <div className="hairline border-l-0 border-t-0 p-4 bg-card">
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{label}</div>
    <div className="font-serif-display text-sm break-all mt-1" data-testid={testid}>{value}</div>
  </div>
);
